import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import { useData } from '../context/DataContext';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Coins, CircleCheck as CheckCircle, CircleAlert as AlertCircle, ArrowLeft, Gift, TrendingUp, TrendingDown, Trophy, Clock, FileText, Mic, Loader as Loader2, RefreshCw, Calendar, Zap, UserPlus, Star } from 'lucide-react';
import { CoinTransaction, SubscriptionPlan, UserSubscription, TokenBatch } from '../services/VirtualCurrencyService';
import { supabase } from '../lib/supabase';
import { dispatchCoinBalanceChanged } from '../lib/coinEvents';
import { trackPurchase, trackInitiateCheckout } from '../lib/analytics';

interface CoinPackage {
  id: string;
  name: string;
  coin_amount: number;
  price_cents: number;
  stripe_price_id: string;
  tag: string | null;
  sort_order: number;
}

type PurchaseTab = 'subscriptions' | 'one-time';

export const PurchaseCoins: React.FC = () => {
  const { user } = useUser();
  const { virtualCurrencyService } = useData();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<CoinTransaction[]>([]);
  const [songTitles, setSongTitles] = useState<Record<string, string>>({});
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [coinPackages, setCoinPackages] = useState<CoinPackage[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [activeSubscription, setActiveSubscription] = useState<UserSubscription | null>(null);
  const [tokenBatches, setTokenBatches] = useState<TokenBatch[]>([]);
  const [activeTab, setActiveTab] = useState<PurchaseTab>('subscriptions');

  useEffect(() => {
    loadPackages();
    loadSubscriptionPlans();
  }, []);

  useEffect(() => {
    if (user) {
      loadTransactionHistory();
      loadSubscriptionData();
    }
  }, [user]);

  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setSuccess(true);
      dispatchCoinBalanceChanged();
      trackPurchase('Coin Package', 0, 'USD');
    }
    if (searchParams.get('canceled') === 'true') {
      setError('Payment was canceled. You have not been charged.');
    }
  }, [searchParams]);

  const loadPackages = async () => {
    try {
      setLoadingPackages(true);
      const { data, error } = await supabase
        .from('coin_packages')
        .select('id, name, coin_amount, price_cents, stripe_price_id, tag, sort_order')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setCoinPackages(data || []);
    } catch (err) {
      console.error('Error loading packages:', err);
      setError('Failed to load coin packages.');
    } finally {
      setLoadingPackages(false);
    }
  };

  const loadSubscriptionPlans = async () => {
    try {
      setLoadingPlans(true);
      const plans = await virtualCurrencyService.getSubscriptionPlans();
      setSubscriptionPlans(plans);
    } catch (err) {
      console.error('Error loading subscription plans:', err);
    } finally {
      setLoadingPlans(false);
    }
  };

  const loadSubscriptionData = async () => {
    if (!user) return;
    try {
      const [sub, batches] = await Promise.all([
        virtualCurrencyService.getActiveSubscription(user.id),
        virtualCurrencyService.getUserTokenBatches(user.id),
      ]);
      setActiveSubscription(sub);
      setTokenBatches(batches);
    } catch (err) {
      console.error('Error loading subscription data:', err);
    }
  };

  const loadTransactionHistory = async () => {
    if (!user) return;
    setLoadingTransactions(true);
    try {
      const history = await virtualCurrencyService.getTransactionHistory(user.id);
      setTransactions(history);

      const songUnlockIds = history
        .filter(t => t.transaction_type === 'purchase' && t.description === 'Song unlock' && t.reference_id)
        .map(t => t.reference_id as string);

      if (songUnlockIds.length > 0) {
        const { data: songs } = await supabase
          .from('songs')
          .select('id, title')
          .in('id', songUnlockIds);

        if (songs) {
          const map: Record<string, string> = {};
          songs.forEach(s => { map[s.id] = s.title; });
          setSongTitles(map);
        }
      }
    } catch (err) {
      console.error('Error loading transaction history:', err);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const getTransactionIcon = (type: CoinTransaction['transaction_type']) => {
    switch (type) {
      case 'purchase':
        return <Coins size={16} className="text-green-400" />;
      case 'subscription':
        return <RefreshCw size={16} className="text-teal-400" />;
      case 'admin_gift':
        return <Gift size={16} className="text-blue-400" />;
      case 'reward':
        return <Trophy size={16} className="text-yellow-400" />;
      case 'tournament_entry':
        return <TrendingDown size={16} className="text-red-400" />;
      case 'tournament_win':
        return <Trophy size={16} className="text-yellow-400" />;
      case 'admin_deduction':
        return <TrendingDown size={16} className="text-red-400" />;
      case 'performance_scoring':
        return <TrendingUp size={16} className="text-green-400" />;
      case 'referral_gift':
        return <UserPlus size={16} className="text-teal-400" />;
      case 'referral_subscriber_bonus':
        return <Star size={16} className="text-amber-400" />;
      default:
        return <Coins size={16} className="text-gray-400" />;
    }
  };

  const getTransactionLabel = (type: CoinTransaction['transaction_type'], description?: string | null) => {
    if (description?.startsWith('Referral Gift')) return 'Referral Gift';
    if (description?.startsWith('Referral Subscriber Bonus')) return 'Referral Subscriber Bonus Gift';
    switch (type) {
      case 'purchase':
        return 'Purchase';
      case 'subscription':
        return 'Subscription';
      case 'admin_gift':
        return 'Admin Gift';
      case 'reward':
        return 'Reward';
      case 'tournament_entry':
        return 'Tournament Entry';
      case 'tournament_win':
        return 'Tournament Win';
      case 'admin_deduction':
        return 'Admin Deduction';
      case 'performance_scoring':
        return 'Performance Reward';
      case 'referral_gift':
        return 'Referral Gift';
      case 'referral_subscriber_bonus':
        return 'Referral Subscriber Bonus Gift';
      default:
        return type;
    }
  };

  const formatPrice = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const handlePurchase = async () => {
    if (!user || !selectedPackage) return;

    const pkg = coinPackages.find(p => p.id === selectedPackage);
    if (!pkg) return;

    setLoading(true);
    setError(null);
    trackInitiateCheckout(pkg.name, pkg.price_cents);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout`;
      const currentUrl = window.location.origin + window.location.pathname;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          package_id: pkg.id,
          success_url: `${currentUrl}?success=true`,
          cancel_url: `${currentUrl}?canceled=true`,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to create checkout session');
      }

      if (result.url) {
        window.location.href = result.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Error initiating checkout:', err);
      setError(err instanceof Error ? err.message : 'Failed to start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!user || !selectedPlan) return;

    const plan = subscriptionPlans.find(p => p.id === selectedPlan);
    if (!plan) return;

    setLoading(true);
    setError(null);
    trackInitiateCheckout(plan.name, plan.price_cents);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-subscription-checkout`;
      const currentUrl = window.location.origin + window.location.pathname;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan_id: plan.id,
          success_url: `${currentUrl}?success=true`,
          cancel_url: `${currentUrl}?canceled=true`,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to create subscription checkout');
      }

      if (result.url) {
        window.location.href = result.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Error initiating subscription:', err);
      setError(err instanceof Error ? err.message : 'Failed to start subscription checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getExpiringTokenCount = () => {
    const now = new Date();
    const thirtyDays = new Date(now);
    thirtyDays.setDate(thirtyDays.getDate() + 30);

    return tokenBatches
      .filter(b => new Date(b.expires_at) <= thirtyDays && b.remaining_tokens > 0)
      .reduce((sum, b) => sum + b.remaining_tokens, 0);
  };

  return (
    <div className="container mx-auto p-6">
      <button
        onClick={() => navigate(-1)}
        className="mb-6 flex items-center text-white/70 hover:text-white transition-colors"
      >
        <ArrowLeft size={20} className="mr-2" />
        Back
      </button>

      <h1 className="text-2xl font-bold mb-2">TSL Tokens & Subscriptions</h1>
      <p className="text-white/70 mb-6">
        Subscribe for recurring tokens or buy one-time coin packs.
      </p>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6 flex items-start">
          <AlertCircle className="text-red-500 mr-3 mt-0.5 flex-shrink-0" size={18} />
          <p className="text-red-500 text-sm">{error}</p>
        </div>
      )}

      {success ? (
        <div className="card text-center py-10">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle size={32} className="text-green-500" />
            </div>
          </div>
          <h2 className="text-xl font-bold mb-2">Purchase Successful!</h2>
          <p className="text-white/70 mb-6">
            Your tokens have been added to your account.
          </p>
          <div className="flex justify-center gap-4">
            <Link to="/profile" className="btn-primary">
              Back to Profile
            </Link>
            <button
              onClick={() => { setSuccess(false); navigate('/purchase-coins', { replace: true }); }}
              className="px-4 py-2 bg-kod-purple-700 hover:bg-kod-purple-600 text-white rounded-lg transition-colors"
            >
              Buy More
            </button>
          </div>
        </div>
      ) : (
        <>
          {activeSubscription && (
            <div className="card mb-6 border border-teal-500/30 bg-teal-500/5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-teal-500/20 flex items-center justify-center">
                    <RefreshCw size={18} className="text-teal-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Active Subscription</h3>
                    <p className="text-sm text-white/60">
                      {activeSubscription.plan_type === 'weekly' ? 'Weekly' : activeSubscription.plan_type === 'monthly' ? 'Monthly' : 'Annual'} Plan
                      {activeSubscription.platform !== 'web' && ` (via ${activeSubscription.platform})`}
                    </p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-teal-500/20 text-teal-400 text-xs font-semibold rounded-full uppercase">
                  Active
                </span>
              </div>
              {activeSubscription.current_period_end && (
                <p className="text-sm text-white/50">
                  Next renewal: {new Date(activeSubscription.current_period_end).toLocaleDateString()}
                </p>
              )}
              {tokenBatches.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium text-white/70">Token Batches</p>
                  {tokenBatches.map((batch) => {
                    const isExpiringSoon = new Date(batch.expires_at) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                    return (
                      <div key={batch.id} className="flex items-center justify-between bg-kod-purple-700/50 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2">
                          <Coins size={14} className="text-yellow-400" />
                          <span className="text-sm font-medium">{batch.remaining_tokens} / {batch.token_amount} remaining</span>
                        </div>
                        <span className={`text-xs ${isExpiringSoon ? 'text-orange-400' : 'text-white/50'}`}>
                          Expires {new Date(batch.expires_at).toLocaleDateString()}
                        </span>
                      </div>
                    );
                  })}
                  {getExpiringTokenCount() > 0 && (
                    <p className="text-xs text-orange-400 mt-1">
                      {getExpiringTokenCount()} tokens expiring within 30 days
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2 mb-6">
            <button
              className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
                activeTab === 'subscriptions'
                  ? 'bg-teal-600 text-white shadow-lg shadow-teal-600/20'
                  : 'bg-kod-purple-700 text-white/60 hover:text-white'
              }`}
              onClick={() => setActiveTab('subscriptions')}
            >
              <div className="flex items-center gap-2">
                <RefreshCw size={16} />
                Subscriptions
              </div>
            </button>
            <button
              className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
                activeTab === 'one-time'
                  ? 'bg-kod-accent text-white shadow-lg shadow-kod-accent/20'
                  : 'bg-kod-purple-700 text-white/60 hover:text-white'
              }`}
              onClick={() => setActiveTab('one-time')}
            >
              <div className="flex items-center gap-2">
                <Coins size={16} />
                One-Time Packs
              </div>
            </button>
          </div>

          {activeTab === 'subscriptions' ? (
            <SubscriptionSection
              plans={subscriptionPlans}
              loadingPlans={loadingPlans}
              selectedPlan={selectedPlan}
              setSelectedPlan={setSelectedPlan}
              activeSubscription={activeSubscription}
              loading={loading}
              handleSubscribe={handleSubscribe}
              formatPrice={formatPrice}
            />
          ) : (
            <OneTimeSection
              packages={coinPackages}
              loadingPackages={loadingPackages}
              selectedPackage={selectedPackage}
              setSelectedPackage={setSelectedPackage}
              loading={loading}
              handlePurchase={handlePurchase}
              formatPrice={formatPrice}
            />
          )}
        </>
      )}

      <div className="card mt-6">
        <h2 className="text-lg font-semibold mb-4">Transaction History</h2>

        {loadingTransactions ? (
          <div className="text-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
            <p className="text-white/70">Loading transactions...</p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-10">
            <Clock size={32} className="mx-auto mb-4 opacity-50" />
            <p className="text-white/70">No transactions yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {transactions.map((transaction) => (
              <div
                key={transaction.id}
                className="bg-kod-purple-700/50 rounded-lg p-4 flex items-center justify-between hover:bg-kod-purple-700/70 transition-colors"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 rounded-full bg-kod-purple-600 flex items-center justify-center">
                    {getTransactionIcon(transaction.transaction_type)}
                  </div>
                  <div>
                    <div className="font-medium">{getTransactionLabel(transaction.transaction_type, transaction.description)}</div>
                    {transaction.description === 'Song unlock' && transaction.reference_id && songTitles[transaction.reference_id] ? (
                      <div className="text-sm text-white/70">{songTitles[transaction.reference_id]}</div>
                    ) : transaction.description ? (
                      <div className="text-sm text-white/70">{transaction.description}</div>
                    ) : null}
                    <div className="text-xs text-white/50">
                      {new Date(transaction.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className={`text-lg font-bold ${transaction.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {transaction.amount >= 0 ? '+' : ''}{transaction.amount}
                  <Coins size={16} className="inline ml-1" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const SubscriptionSection: React.FC<{
  plans: SubscriptionPlan[];
  loadingPlans: boolean;
  selectedPlan: string | null;
  setSelectedPlan: (id: string | null) => void;
  activeSubscription: UserSubscription | null;
  loading: boolean;
  handleSubscribe: () => void;
  formatPrice: (cents: number) => string;
}> = ({ plans, loadingPlans, selectedPlan, setSelectedPlan, activeSubscription, loading, handleSubscribe, formatPrice }) => {

  const getMonthlyRate = (plan: SubscriptionPlan) => {
    if (plan.billing_interval === 'monthly') return plan.price_cents;
    return Math.round(plan.price_cents / 12);
  };

  const getSavingsPercent = (plans: SubscriptionPlan[]) => {
    const monthly = plans.find(p => p.billing_interval === 'monthly');
    const annual = plans.find(p => p.billing_interval === 'annual');
    if (!monthly || !annual) return 0;
    const monthlyCostAnnual = monthly.price_cents * 12;
    return Math.round(((monthlyCostAnnual - annual.price_cents) / monthlyCostAnnual) * 100);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <div className="card mb-6">
          <h2 className="text-lg font-semibold mb-1">Token Subscriptions</h2>
          <p className="text-white/50 text-sm mb-5">
            Get tokens on a recurring schedule with rollover for unused tokens.
          </p>

          {loadingPlans ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
            </div>
          ) : plans.length === 0 ? (
            <div className="text-center py-10 text-white/50">
              <RefreshCw size={32} className="mx-auto mb-3 opacity-50" />
              <p>No subscription plans available at this time.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {plans.map((plan) => {
                const isAnnual = plan.billing_interval === 'annual';
                const isWeekly = plan.billing_interval === 'weekly';
                const savings = getSavingsPercent(plans);
                const periodLabel = isWeekly ? 'week' : 'month';

                return (
                  <div
                    key={plan.id}
                    className={`border rounded-lg p-5 transition-all cursor-pointer relative ${
                      selectedPlan === plan.id
                        ? 'border-teal-500 bg-teal-500/10 shadow-lg shadow-teal-500/10'
                        : 'border-kod-purple-600 hover:border-kod-purple-500 bg-kod-purple-700/50'
                    } ${activeSubscription ? 'opacity-50 pointer-events-none' : ''}`}
                    onClick={() => !activeSubscription && setSelectedPlan(plan.id)}
                  >
                    {plan.tag && (
                      <div className="absolute -top-3 right-4 bg-teal-500 text-white text-xs py-1 px-3 rounded-full font-bold">
                        {plan.tag}
                      </div>
                    )}
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          isAnnual ? 'bg-teal-500/20' : 'bg-kod-purple-600'
                        }`}>
                          {isAnnual ? (
                            <Calendar size={20} className="text-teal-400" />
                          ) : isWeekly ? (
                            <Clock size={20} className="text-teal-400" />
                          ) : (
                            <RefreshCw size={20} className="text-teal-400" />
                          )}
                        </div>
                        <div>
                          <div className="font-bold text-lg">{plan.name}</div>
                          <div className="text-white/50 text-sm">
                            {plan.token_amount} tokens / {periodLabel}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-xl">{formatPrice(plan.price_cents)}</div>
                        <div className="text-white/50 text-xs">
                          {isAnnual ? `/year (${formatPrice(getMonthlyRate(plan))}/mo)` : isWeekly ? '/week' : '/month'}
                        </div>
                        {isAnnual && savings > 0 && (
                          <div className="text-teal-400 text-xs font-semibold mt-1">
                            Save {savings}%
                          </div>
                        )}
                      </div>
                    </div>
                    {selectedPlan === plan.id && (
                      <div className="absolute top-5 left-5 w-5 h-5 rounded-full bg-teal-500 flex items-center justify-center">
                        <CheckCircle size={14} className="text-white" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold mb-4">How Token Subscriptions Work</h2>
          <div className="space-y-3">
            <div className="flex items-start">
              <div className="w-8 h-8 rounded-full bg-teal-500/10 flex items-center justify-center mr-3 mt-0.5">
                <Zap size={16} className="text-teal-400" />
              </div>
              <div>
                <div className="font-medium">Tokens Every Cycle</div>
                <div className="text-sm text-white/70">Receive tokens each billing period (weekly, monthly, or annually)</div>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-8 h-8 rounded-full bg-teal-500/10 flex items-center justify-center mr-3 mt-0.5">
                <Clock size={16} className="text-teal-400" />
              </div>
              <div>
                <div className="font-medium">1 Cycle Rollover</div>
                <div className="text-sm text-white/70">Unused tokens roll over for 1 additional billing cycle before expiring</div>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-8 h-8 rounded-full bg-teal-500/10 flex items-center justify-center mr-3 mt-0.5">
                <RefreshCw size={16} className="text-teal-400" />
              </div>
              <div>
                <div className="font-medium">Tokens Stack</div>
                <div className="text-sm text-white/70">New tokens are added each cycle while rollover tokens remain until they expire</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card h-fit">
        <h2 className="text-lg font-semibold mb-4">Subscription Summary</h2>

        {activeSubscription ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 rounded-full bg-teal-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={28} className="text-teal-500" />
            </div>
            <p className="font-medium text-lg mb-1">You're already subscribed</p>
            <p className="text-white/50 text-sm mb-4">
              Manage your subscription through your app store or Stripe account.
            </p>
          </div>
        ) : selectedPlan ? (
          <>
            <div className="bg-kod-purple-700/50 rounded-lg p-4 mb-6">
              {(() => {
                const plan = plans.find(p => p.id === selectedPlan);
                if (!plan) return null;
                return (
                  <>
                    <div className="flex justify-between mb-2">
                      <span className="text-white/70">Plan</span>
                      <span className="font-medium">{plan.name}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="text-white/70">Tokens per cycle</span>
                      <span className="font-medium">{plan.token_amount}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="text-white/70">Billing</span>
                      <span className="font-medium capitalize">{plan.billing_interval}</span>
                    </div>
                    <div className="border-t border-kod-purple-600 my-2 pt-2">
                      <div className="flex justify-between">
                        <span className="font-medium">Price</span>
                        <span className="font-bold text-lg">{formatPrice(plan.price_cents)}<span className="text-sm text-white/50 font-normal">/{plan.billing_interval === 'weekly' ? 'wk' : plan.billing_interval === 'monthly' ? 'mo' : 'yr'}</span></span>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>

            <p className="text-white/50 text-xs mb-4">
              You'll be redirected to a secure Stripe checkout page. Your subscription will auto-renew until cancelled.
            </p>

            <button
              className="btn-primary w-full"
              onClick={handleSubscribe}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <Loader2 size={18} className="animate-spin mr-2" />
                  Redirecting to checkout...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <RefreshCw size={18} className="mr-2" />
                  Subscribe Now
                </div>
              )}
            </button>
          </>
        ) : (
          <div className="text-center py-10 text-white/70">
            <RefreshCw size={32} className="mx-auto mb-4 opacity-50" />
            <p>Select a subscription plan to continue</p>
          </div>
        )}
      </div>
    </div>
  );
};

const OneTimeSection: React.FC<{
  packages: CoinPackage[];
  loadingPackages: boolean;
  selectedPackage: string | null;
  setSelectedPackage: (id: string | null) => void;
  loading: boolean;
  handlePurchase: () => void;
  formatPrice: (cents: number) => string;
}> = ({ packages, loadingPackages, selectedPackage, setSelectedPackage, loading, handlePurchase, formatPrice }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <div className="card mb-6">
          <h2 className="text-lg font-semibold mb-4">Select a Package</h2>

          {loadingPackages ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kod-accent"></div>
            </div>
          ) : packages.length === 0 ? (
            <div className="text-center py-10 text-white/50">
              <Coins size={32} className="mx-auto mb-3 opacity-50" />
              <p>No coin packages are available at this time.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {packages.map((pkg) => (
                <div
                  key={pkg.id}
                  className={`border rounded-lg p-4 transition-all cursor-pointer relative ${
                    selectedPackage === pkg.id
                      ? 'border-kod-accent bg-kod-accent/10 shadow-lg shadow-kod-accent/5'
                      : 'border-kod-purple-600 hover:border-kod-purple-500 bg-kod-purple-700/50'
                  }`}
                  onClick={() => setSelectedPackage(pkg.id)}
                >
                  {pkg.tag && (
                    <div className="absolute -top-3 right-4 bg-kod-accent text-white text-xs py-1 px-2 rounded-full font-bold">
                      {pkg.tag}
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-kod-purple-600 flex items-center justify-center mr-3">
                        <Coins size={18} className="text-yellow-400" />
                      </div>
                      <div>
                        <div className="font-bold text-lg">{pkg.coin_amount.toLocaleString()} Coins</div>
                        <div className="text-white/70 text-sm">{formatPrice(pkg.price_cents)}</div>
                      </div>
                    </div>
                    <div>
                      {selectedPackage === pkg.id && (
                        <div className="w-6 h-6 rounded-full bg-kod-accent flex items-center justify-center">
                          <CheckCircle size={16} className="text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold mb-4">What You Can Do With Coins</h2>
          <div className="space-y-3">
            <div className="flex items-start">
              <div className="w-8 h-8 rounded-full bg-kod-purple-700 flex items-center justify-center mr-3 mt-0.5">
                <Trophy size={16} className="text-yellow-400" />
              </div>
              <div>
                <div className="font-medium">Enter Tournaments</div>
                <div className="text-sm text-white/70">Compete with other singers and win prizes</div>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-8 h-8 rounded-full bg-kod-purple-700 flex items-center justify-center mr-3 mt-0.5">
                <Mic size={16} className="text-kod-accent" />
              </div>
              <div>
                <div className="font-medium">Premium Features</div>
                <div className="text-sm text-white/70">Access premium vocal effects and tools</div>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-8 h-8 rounded-full bg-kod-purple-700 flex items-center justify-center mr-3 mt-0.5">
                <FileText size={16} className="text-kod-accent" />
              </div>
              <div>
                <div className="font-medium">Distribution Services</div>
                <div className="text-sm text-white/70">Distribute your performances to streaming platforms</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card h-fit">
        <h2 className="text-lg font-semibold mb-4">Payment Summary</h2>

        {selectedPackage ? (
          <>
            <div className="bg-kod-purple-700/50 rounded-lg p-4 mb-6">
              {(() => {
                const pkg = packages.find(p => p.id === selectedPackage);
                if (!pkg) return null;
                return (
                  <>
                    <div className="flex justify-between mb-2">
                      <span className="text-white/70">Package</span>
                      <span className="font-medium">{pkg.coin_amount.toLocaleString()} Coins</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="text-white/70">Price</span>
                      <span className="font-medium">{formatPrice(pkg.price_cents)}</span>
                    </div>
                    <div className="border-t border-kod-purple-600 my-2 pt-2">
                      <div className="flex justify-between">
                        <span className="font-medium">Total</span>
                        <span className="font-bold">{formatPrice(pkg.price_cents)}</span>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>

            <p className="text-white/50 text-xs mb-4">
              You'll be redirected to a secure Stripe checkout page to complete your purchase.
            </p>

            <button
              className="btn-primary w-full"
              onClick={handlePurchase}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <Loader2 size={18} className="animate-spin mr-2" />
                  Redirecting to checkout...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <Coins size={18} className="mr-2" />
                  Proceed to Checkout
                </div>
              )}
            </button>
          </>
        ) : (
          <div className="text-center py-10 text-white/70">
            <Coins size={32} className="mx-auto mb-4 opacity-50" />
            <p>Select a coin package to continue</p>
          </div>
        )}
      </div>
    </div>
  );
};
